import {
	Battery50Icon,
	ChartBarIcon,
	UserCircleIcon,
	WifiIcon
} from '@heroicons/react/20/solid'

import React from 'react'
import { parseTextToHTML } from '../../../../libs/parse'
import { LINK_BUTTON, PHONE_CALL_BUTTON, REPLY_BUTTON } from './constants'

const Header = () => {
  return (
    <header className="sticky top-0 bg-white shadow z-10 px-4 py-3 text-grey-blue ">
      <div id="phoneInfo" className="flex">
        <p className="text-sm font-bold">1:37</p>
        <div id="phoneStatus" className="ml-auto flex gap-2">
          <WifiIcon className="h-5 w-5 " />
          <ChartBarIcon className="h-5 w-5 " />
          <Battery50Icon className="h-5 w-5 " />
        </div>
      </div>
      <UserCircleIcon className="h-8 w-8 mx-auto" />
    </header>
  )
}

const InteractiveButton = ({ button, onClick, interactive }) => {
  const handleClick = () => {
    if (!interactive) return
    if (button.type === REPLY_BUTTON && button.replyFlow) {
      onClick?.(button)
    } else if (button.type === LINK_BUTTON && button.link) {
      window.open(button.link, '_blank')
    } else if (button.type === PHONE_CALL_BUTTON && button.phone) {
      window.open(`tel:${button.phone}`, '_self')
    }
  }

  const getActionEmoji = (action) => {
    switch (action) {
      case 'send-email':
        return '📧'
      case 'schedule-flow':
        return '📅'
      case 'webhook':
        return '🔗'
      default:
        return '⚡'
    }
  }

  const hasAction = button.sendEmail || button.action || button.scheduleFlow
  const actionText = button.sendEmail
    ? `📧 email[${button.sendEmailIndex || '1'}]`
    : button.action
    ? `${getActionEmoji(button.action)} ${button.action}`
    : button.scheduleFlow
    ? `📅 schedule`
    : null

  return (
    <button
      onClick={handleClick}
      className={`text-indigo-500 px-3 py-2 border-none text-center relative text-sm flex items-center justify-center gap-1 ${
        interactive ? 'cursor-pointer hover:text-indigo-700 hover:bg-indigo-50 transition-colors' : ''
      } ${hasAction ? 'bg-green-50 border border-green-200 rounded' : ''}`}
      title={hasAction ? actionText : ''}
    >
      <span>{button.text}</span>
      {hasAction && <span className="text-xs opacity-70">{actionText}</span>}
    </button>
  )
}

const RichCard = ({ card, isFromCrousel, interactive, onButtonClick }) => {
  return (
    <div
      className={`flex flex-col border-2 border-gray-400 rounded-2xl overflow-hidden ${
        isFromCrousel ? 'min-w-[200px]' : ''
      }`}
    >
      <img
        src={card.image.preview}
        alt={card.title || 'image'}
        style={{ aspectRatio: !isFromCrousel ? 2 / 1 : 4 / 3 }}
        className="w-full object-cover "
      />
      <div
        id="cardInfo"
        className="flex flex-col bg-white px-4 py-2 min-h-[80px] justify-between"
      >
        <div>
          {card.title && <h2 className="text-lg font-bold">{card.title}</h2>}
          {card.description && <p className="text-sm">{card.description}</p>}
        </div>
        {card.buttons && card.buttons.length > 0 && (
          <div className="flex flex-col">
            {card.buttons.map((button, idx) => (
              <InteractiveButton
                key={button.text || idx}
                button={button}
                onClick={onButtonClick}
                interactive={interactive}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

const Carousel = ({ carousel, interactive, onButtonClick }) => {
  return (
    <div className="flex flex-col gap-4">
      <div
        id="cards"
        className="flex gap-2 -mx-4 pl-4 overflow-x-auto scrollbar-styled"
      >
        {carousel.cards.map((card, idx) => (
          <RichCard 
            key={card.title || idx} 
            card={card} 
            isFromCrousel 
            interactive={interactive}
            onButtonClick={onButtonClick}
          />
        ))}
      </div>
    </div>
  )
}

const TextMessage = ({ message, isFirst }) => {
  return (
    <div className="flex gap-2">
      {isFirst && <UserCircleIcon className="h-8 w-8 text-grey-blue" />}
      <div className="bg-blue-light/25 rounded-2xl p-2 min-w-[40px]">
        <p className="w-fit">{parseTextToHTML(message.text)}</p>
      </div>
    </div>
  )
}

const Suggestions = ({ suggestions, interactive, onButtonClick }) => {
  const handleClick = (suggestion) => {
    if (!interactive) return
    if (suggestion.type === REPLY_BUTTON && suggestion.replyFlow) {
      onButtonClick?.(suggestion)
    } else if (suggestion.type === LINK_BUTTON && suggestion.link) {
      window.open(suggestion.link, '_blank')
    } else if (suggestion.type === PHONE_CALL_BUTTON && suggestion.phone) {
      window.open(`tel:${suggestion.phone}`, '_self')
    }
  }

  return (
    <div className="-mx-4">
      <div
        id="suggestions"
        className="flex px-4 ml-auto w-fit max-w-full gap-2 self-end overflow-x-auto scrollbar-styled "
      >
        {suggestions.map((suggestion, idx) => (
          <button
            key={suggestion.text || idx}
            onClick={() => handleClick(suggestion)}
            className={`border-2 border-gray-400 px-4 py-1 text-center rounded-full whitespace-nowrap ${
              interactive 
                ? 'cursor-pointer hover:border-indigo-500 hover:text-indigo-600 hover:bg-indigo-50 transition-colors' 
                : ''
            }`}
          >
            {suggestion.text}
          </button>
        ))}
      </div>
    </div>
  )
}

const EmptyFlow = () => {
  return (
    <div className="flex flex-col items-center justify-center w-full bg-blue-light/25 rounded-2xl p-2">
      <p className="text-lg font-bold">No elements</p>
      <p className="text-sm">Add elements to visualize them</p>
    </div>
  )
}

const getElements = (elements, interactive = false, onButtonClick = null) => {
  let rbmElements = []
  if (elements.length === 0) {
    rbmElements.push(<EmptyFlow key="empty" />)
    return rbmElements
  }
  rbmElements = elements.map((element, index) => {
    switch (element.type) {
      case 'TEXT_TYPE':
        return (
          <TextMessage
            key={element.name || index}
            message={element}
            isFirst={index === 0}
          />
        )
      case 'CARD_TYPE':
        return (
          <RichCard 
            key={element.name || index} 
            card={element} 
            interactive={interactive}
            onButtonClick={onButtonClick}
          />
        )
      case 'CAROUSEL_TYPE':
        return (
          <>
            {element.text && (
              <TextMessage
                key={`${element.name || index}-text`}
                message={element}
                isFirst={index === 0}
              />
            )}
            <Carousel 
              key={element.name || index} 
              carousel={element} 
              interactive={interactive}
              onButtonClick={onButtonClick}
            />
          </>
        )
      default:
        return null
    }
  })

  const lastElement = elements[elements.length - 1]
  if (
    lastElement?.suggestions &&
    lastElement.suggestions.length > 0
  ) {
    rbmElements.push(
      <Suggestions
        key="suggestions"
        suggestions={lastElement.suggestions}
        interactive={interactive}
        onButtonClick={onButtonClick}
      />
    )
  }

  return rbmElements
}

/**
 * PhonePreview component - displays RBM messages in a phone mockup
 * @param {Array} rbmElements - Array of RBM elements to display
 * @param {boolean} interactive - Enable interactive mode with clickable buttons
 * @param {Function} onButtonClick - Callback when a button is clicked (interactive mode)
 * @param {string} label - Optional label to show at bottom of phone
 */
const PhonePreview = ({ 
  rbmElements = [], 
  interactive = false, 
  onButtonClick = null,
  label = null
}) => {
  return (
    <div className="flex flex-col relative phone w-full max-w-[300px] min-w-[300px] border-4 border-grey-blue/30 rounded-3xl shadow-2xl bg-white overflow-hidden">
      <Header />
      <div className="h-full overflow-auto scrollbar-styled">
        <div id="messages" className="flex flex-col p-4 gap-2 scrollbar-styled">
          {getElements(rbmElements, interactive, onButtonClick)}
        </div>
      </div>
      {label && (
        <div className="bg-gray-100 text-gray-600 text-center py-1.5 text-xs font-medium border-t border-gray-200">
          {label}
        </div>
      )}
    </div>
  )
}

export default PhonePreview
